import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

/* ── Download history callback ──────────────────────── */
let _historyCallback = null;
export function setDownloadHistoryCallback(cb) { _historyCallback = cb; }
function logDownload(filename, format, source) {
  if (_historyCallback) _historyCallback({ filename, format, source });
}

/* Helper: wraps autoTable to work as both function-style and tracks finalY */
function addTable(doc, opts) {
  autoTable(doc, opts);
  return doc;
}
function lastTableY(doc) {
  // jspdf-autotable stores info on doc after autoTable call
  if (doc.lastAutoTable) return doc.lastAutoTable.finalY;
  if (doc.previousAutoTable) return doc.previousAutoTable.finalY;
  return 70;
}

/* ── Shared Helpers ─────────────────────────────────── */
function apexHeader(doc, title, subtitle) {
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(40, 40, 60);
  doc.text('APEX', 14, 18);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(120, 120, 140);
  doc.text('Temporal AI Governance \u00B7 Proof Infrastructure', 14, 24);
  doc.text('Meridian Global — Zurich · Production', 14, 29);
  doc.setDrawColor(99, 102, 241);
  doc.setLineWidth(0.5);
  doc.line(14, 32, 196, 32);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(40, 40, 60);
  doc.text(title, 14, 42);
  if (subtitle) {
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100, 100, 120);
    doc.text(subtitle, 14, 48);
  }
  doc.setFontSize(8);
  doc.setTextColor(140, 140, 160);
  doc.text(`Generated: ${new Date().toISOString().slice(0, 19).replace('T', ' ')} UTC`, 14, subtitle ? 54 : 48);
  return subtitle ? 60 : 54;
}

function apexFooter(doc) {
  const pages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(7);
    doc.setTextColor(160, 160, 180);
    doc.text('Apex provides AI decision evidence. Your auditor provides the attestation opinion.', 14, 287);
    doc.text(`Page ${i} of ${pages}`, 186, 287, { align: 'right' });
  }
}

function downloadCSV(filename, headers, rows, source) {
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
  const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8' });
  saveAs(blob, filename);
  logDownload(filename, 'csv', source || filename);
}

/* ── Incident Review Exports ─────────────────────────── */
const affectedDecisions = Array.from({ length: 50 }, (_, i) => ({
  id: `DER-2026-${String(47832 - i).padStart(6, '0')}`,
  timestamp: `2026-04-15T${String(14 - Math.floor(i / 20)).padStart(2, '0')}:${String(31 - (i % 30)).padStart(2, '0')}:07Z`,
  model: 'GPT-4o 2025-11',
  workflow: 'Contract Classification Pipeline',
  principal: 'contract-processor@meridian.global',
  output: `Classification → Category Code G0${10 + (i % 8)}`,
  status: i < 3 ? 'Under Review' : 'Verified',
}));

export function exportAffectedListCSV() {
  downloadCSV('apex-affected-decisions.csv',
    ['Record ID', 'Timestamp', 'Model', 'Workflow', 'Principal', 'Output', 'Status'],
    affectedDecisions.map(d => [d.id, d.timestamp, d.model, d.workflow, d.principal, d.output, d.status]),
    'Incident Review — Affected Decisions'
  );
}

export function exportResponsePackagePDF(packageTitle) {
  const doc = new jsPDF();
  const y = apexHeader(doc, packageTitle || 'Response Package', 'Incident: Unauthorized model parameter change — Legal Document Drafting');
  addTable(doc, {
    startY: y + 2,
    head: [['Field', 'Value']],
    body: [
      ['Investigation ID', 'INC-2026-0415-001'],
      ['Opened', 'April 15, 2026 · 14:31 UTC'],
      ['Package Generated', 'April 15, 2026 · 15:00 UTC'],
      ['Total Response Time', '3 minutes 47 seconds'],
      ['Affected Decisions', '8,203'],
      ['Evidence Coverage', '100%'],
      ['Models Affected', 'Claude Sonnet 3.7 — Legal Document Drafting'],
      ['Root Cause', 'Unauthorized parameter change to temperature setting'],
      ['Status', 'Active Investigation'],
    ],
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [99, 102, 241] },
  });
  addTable(doc, {
    startY: lastTableY(doc) + 8,
    head: [['Control', 'Status at Time of Event', 'Evidence']],
    body: [
      ['Model registry & version lock', 'Active', 'Version hash verified'],
      ['Change approval workflow', 'Partial — bypassed', 'Approval record missing'],
      ['Automated output monitoring', 'Active', 'Drift detected at 14:28 UTC'],
      ['Decision Evidence Recording', 'Active', '100% DER coverage maintained'],
      ['Lineage verification', 'Partial — upstream gap', 'External Legal Corpus unverified'],
    ],
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [99, 102, 241] },
  });
  apexFooter(doc);
  const fn = `apex-${(packageTitle || 'response-package').toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`;
  doc.save(fn);
  logDownload(fn, 'pdf', `Incident Review — ${packageTitle || 'Response Package'}`);
}

export function exportGenerateResponsePackagePDF() {
  exportResponsePackagePDF('Complete Response Package');
}

/* ── Decision Record Detail Export ────────────────────── */
export function exportDecisionRecordPDF(record) {
  const doc = new jsPDF();
  const y = apexHeader(doc, `Decision Evidence Record: ${record.id}`, `${record.workflow || 'Contract Classification Pipeline'} · ${record.model || 'GPT-4o 2025-11'}`);
  addTable(doc, {
    startY: y + 2,
    head: [['Field', 'Value']],
    body: [
      ['Record ID', record.id],
      ['Timestamp', record.timestamp || 'April 15, 2026 · 14:32:07 UTC'],
      ['Model', record.model || 'GPT-4o (Azure OpenAI) · Version 2025-11'],
      ['Workflow', record.workflow || 'Contract Classification Pipeline'],
      ['Principal', record.principal || 'contract-processor@meridian.global'],
      ['Output Type', record.outputType || 'Classification'],
      ['Signature', record.signature || 'sha256:a3f9e2c1d8b7e4f690ab12cd34ef56gh7…'],
      ['Verification', record.verificationStatus || 'Verified · Compliant'],
    ],
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [99, 102, 241] },
  });
  addTable(doc, {
    startY: lastTableY(doc) + 8,
    head: [['Processing Integrity Check', 'Status', 'Detail']],
    body: [
      ['Output validation', 'Verified', 'Output format matches expected schema'],
      ['Human review status', 'Reviewed', 'Contract Analyst ID CA-4471 confirmed'],
      ['Source data verification', 'Verified', 'All 3 data sources verified at invocation time'],
      ['Model version at decision time', 'Verified', 'GPT-4o 2025-11 · hash verified'],
      ['Post-decision exception flag', 'None', 'No exceptions'],
    ],
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [16, 185, 129] },
  });
  addTable(doc, {
    startY: lastTableY(doc) + 8,
    head: [['Framework', 'Control / Criteria', 'Evidence Field']],
    body: [
      ['EU AI Act', 'Art. 9 Risk Management', 'Risk classification field'],
      ['EU AI Act', 'Art. 13 Transparency', 'Model identity + version hash'],
      ['EU AI Act', 'Art. 14 Human Oversight', 'Human reviewer field'],
      ['ISO 42001', '6.1 Risk assessment', 'Data source verification'],
      ['ISO 42001', '9.1 Monitoring', 'Cryptographic signature + timestamp'],
      ['NIST AI RMF', 'GOVERN 1.1', 'Principal identity field'],
      ['NIST AI RMF', 'MANAGE 2.2', 'Output type + confidence'],
      ['SOC 2', 'PI1.1 Processing Integrity', 'Output validation status'],
      ['SOC 2', 'CC6.1 Logical Access', 'Principal + data access record'],
    ],
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [99, 102, 241] },
  });
  apexFooter(doc);
  doc.save(`apex-${record.id}.pdf`);
  logDownload(`apex-${record.id}.pdf`, 'pdf', `Decision Record — ${record.id}`);
}

/* ── RACI Matrix Export ──────────────────────────────── */
export function exportRACIPDF() {
  const doc = new jsPDF('landscape');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(16);
  doc.text('APEX — RACI Matrix: AI Governance Roles', 14, 18);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100);
  doc.text(`Meridian Global · Generated ${new Date().toISOString().slice(0, 10)}`, 14, 24);
  addTable(doc, {
    startY: 30,
    head: [['Function', 'CISO', 'AI Ethics Board', 'Engineering Lead', 'Legal Counsel', 'Data Protection Officer']],
    body: [
      ['Model Registration', 'A', 'C', 'R', 'I', 'I'],
      ['Risk Classification', 'A', 'R', 'C', 'C', 'I'],
      ['Change Approval', 'R', 'A', 'R', 'I', 'I'],
      ['Evidence Collection', 'I', 'I', 'R', 'I', 'A'],
      ['Incident Response', 'R', 'C', 'R', 'A', 'C'],
      ['Regulatory Reporting', 'A', 'C', 'I', 'R', 'R'],
      ['Audit Preparation', 'A', 'I', 'C', 'C', 'R'],
      ['Board Reporting', 'R', 'A', 'I', 'C', 'I'],
    ],
    theme: 'grid',
    styles: { fontSize: 8, halign: 'center', cellPadding: 4 },
    headStyles: { fillColor: [99, 102, 241] },
    columnStyles: { 0: { halign: 'left', fontStyle: 'bold' } },
  });
  apexFooter(doc);
  doc.save('apex-raci-matrix.pdf');
  logDownload('apex-raci-matrix.pdf', 'pdf', 'Roles & Responsibilities — RACI Matrix');
}

/* ── Assurance Center Package Exports ─────────────────── */
export function exportAssurancePackagePDF(pkg) {
  const doc = new jsPDF();
  const y = apexHeader(doc, pkg.title, pkg.desc.slice(0, 100) + '…');
  addTable(doc, {
    startY: y + 2,
    head: [['Metric', 'Value']],
    body: [
      ['Evidence Completeness', `${pkg.completeness}%`],
      ['Records Included', pkg.records.toLocaleString()],
      ['Last Generated', pkg.lastGen],
      ['Package Type', pkg.title],
    ],
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [99, 102, 241] },
  });
  const sampleControls = [
    ['CC6.1', 'Logical access controls for AI systems', '100%', 'Ready'],
    ['PI1.1', 'Processing integrity of AI outputs', '94%', 'Partial'],
    ['PI1.2', 'Complete and accurate AI output recording', '99.5%', 'Ready'],
    ['CM-1', 'Change management for AI systems', '93%', 'Ready'],
    ['IR-1', 'Incident response for AI events', '100%', 'Ready'],
  ];
  addTable(doc, {
    startY: lastTableY(doc) + 10,
    head: [['Control ID', 'Description', 'Coverage', 'Status']],
    body: sampleControls,
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [99, 102, 241] },
  });
  if (pkg.disclaimer) {
    doc.setFontSize(7);
    doc.setTextColor(140, 140, 160);
    doc.text(pkg.disclaimer, 14, lastTableY(doc) + 10);
  }
  apexFooter(doc);
  const fn = `apex-${pkg.id || pkg.title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`;
  doc.save(fn);
  logDownload(fn, 'pdf', `Assurance Center — ${pkg.title}`);
}

/* ── Risk Score Roadmap Export ────────────────────────── */
export function exportRemediationRoadmapPDF() {
  const doc = new jsPDF();
  const y = apexHeader(doc, 'Remediation Roadmap', 'AI Security Posture Improvement Plan');
  addTable(doc, {
    startY: y + 2,
    head: [['Priority', 'Risk Factor', 'Current Score', 'Target', 'Owner', 'Due Date']],
    body: [
      ['P0', 'Unverified data source — External Legal Corpus', '41/100', '85/100', 'Data Governance', 'Apr 30, 2026'],
      ['P0', 'Fine-tune pipeline provenance gap', '67/100', '90/100', 'ML Engineering', 'May 15, 2026'],
      ['P1', 'EU AI Act transparency documentation', '68%', '95%', 'Legal & Compliance', 'Jun 30, 2026'],
      ['P1', 'SOC 2 AI evidence gaps (8 controls)', '61%', '90%', 'IT Audit', 'Jul 31, 2026'],
      ['P2', 'ISO 42001 certification readiness', '55%', '80%', 'Quality Management', 'Sep 30, 2026'],
      ['P2', 'GPT-4o Mini — unaudited model', '0%', '70%', 'ML Engineering', 'Aug 15, 2026'],
    ],
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [99, 102, 241] },
  });
  apexFooter(doc);
  doc.save('apex-remediation-roadmap.pdf');
  logDownload('apex-remediation-roadmap.pdf', 'pdf', 'Risk Score — Remediation Roadmap');
}

/* ── Evidence Vault: Integrity Manifest ───────────────── */
export function exportIntegrityManifestPDF() {
  const doc = new jsPDF();
  const y = apexHeader(doc, 'Evidence Vault Integrity Manifest', 'Cryptographic verification report');
  addTable(doc, {
    startY: y + 2,
    head: [['Metric', 'Value']],
    body: [
      ['Total Records', '47,832'],
      ['Verified & Signed', '47,614'],
      ['Integrity Warnings', '218'],
      ['Verification Method', 'SHA-256 hash chain'],
      ['Last Sweep', new Date().toISOString().slice(0, 19).replace('T', ' ') + ' UTC'],
      ['Manifest Hash', 'sha256:f2a19c8b7e4d...'],
    ],
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [16, 185, 129] },
  });
  apexFooter(doc);
  doc.save('apex-integrity-manifest.pdf');
  logDownload('apex-integrity-manifest.pdf', 'pdf', 'Evidence Vault — Integrity Manifest');
}

/* ── Auditor Workspace Exports ────────────────────────── */
const populationRows = Array.from({ length: 100 }, (_, i) => [
  `DER-2026-${String(47832 - i).padStart(6, '0')}`,
  `2026-04-${String(15 - Math.floor(i / 50)).padStart(2, '0')}T${String(14 - (i % 14)).padStart(2, '0')}:${String(31 - (i % 30)).padStart(2, '0')}:07Z`,
  i % 5 === 0 ? 'Claude Sonnet 3.7' : 'GPT-4o 2025-11',
  i % 5 === 0 ? 'Legal Document Drafting' : 'Contract Classification',
  i < 5 ? 'PI1.1' : i < 20 ? 'CC6.1' : i < 40 ? 'PI1.2' : i < 60 ? 'CM-1' : 'IR-1',
  i < 3 ? 'Warning' : 'Verified',
]);

export function exportPopulationCSV() {
  downloadCSV('apex-audit-population.csv',
    ['Record ID', 'Timestamp', 'Model', 'Workflow', 'Control Mapping', 'Status'],
    populationRows,
    'Auditor Workspace — Population'
  );
}

export function exportExceptionReportPDF() {
  const doc = new jsPDF();
  const y = apexHeader(doc, 'Exception Report', 'Integrity warnings, control gaps, and open exceptions');
  addTable(doc, {
    startY: y + 2,
    head: [['Exception ID', 'Type', 'Description', 'Severity', 'Status', 'Control']],
    body: [
      ['EXC-001', 'Integrity Warning', 'Hash mismatch — DER-2026-031204 — single-bit divergence', 'Medium', 'Open', 'PI1.2'],
      ['EXC-002', 'Control Gap', 'External Legal Corpus provenance not established', 'High', 'Open', 'PI1.1'],
      ['EXC-003', 'Control Gap', 'Fine-tune pipeline upstream dependency unverified', 'High', 'Open', 'PI1.1'],
      ['EXC-004', 'Policy Exception', 'GPT-4o Mini operating without audit trail', 'Critical', 'Pending Approval', 'CC6.1'],
      ['EXC-005', 'Integrity Warning', '218 DERs with minor timestamp drift (< 2ms)', 'Low', 'Monitoring', 'PI1.2'],
      ['EXC-006', 'Control Gap', 'EU AI Act Art. 13 transparency docs incomplete — 68%', 'High', 'In Progress', 'EU-AI-13.1'],
      ['EXC-007', 'Control Gap', 'ISO 42001 risk assessment documentation — 55%', 'Medium', 'In Progress', 'ISO-6.1'],
      ['EXC-008', 'Control Gap', 'SOC 2 Processing Integrity evidence — 94%', 'Low', 'In Progress', 'PI1.1'],
    ],
    theme: 'grid',
    styles: { fontSize: 7, cellPadding: 2.5 },
    headStyles: { fillColor: [245, 158, 11] },
  });
  apexFooter(doc);
  doc.save('apex-exception-report.pdf');
  logDownload('apex-exception-report.pdf', 'pdf', 'Auditor Workspace — Exception Report');
}

export function exportInternalAuditPackage() {
  const doc = new jsPDF();
  const y = apexHeader(doc, 'Internal Audit Package', 'AI Governance Evidence for Internal Audit Teams');
  addTable(doc, {
    startY: y + 2,
    head: [['Section', 'Contents']],
    body: [
      ['1. Population Summary', '47,832 Decision Evidence Records · Jan 1 – Apr 15, 2026'],
      ['2. Control Matrix', '23/31 controls covered · 8 with evidence gaps'],
      ['3. Sampling Guide', 'Recommended sample: 384 records (95% confidence, 5% margin)'],
      ['4. Exception Register', '8 open exceptions · 3 high severity · see Section 5'],
      ['5. Governance Narrative', 'AI governance structure, roles (RACI), and oversight processes'],
    ],
    theme: 'grid',
    styles: { fontSize: 9, cellPadding: 4 },
    headStyles: { fillColor: [99, 102, 241] },
  });
  addTable(doc, {
    startY: lastTableY(doc) + 8,
    head: [['Control ID', 'Framework', 'Description', 'Coverage', 'Status']],
    body: [
      ['CC6.1', 'SOC 2', 'Logical access controls for AI systems', '100%', 'Ready'],
      ['PI1.1', 'SOC 2', 'Processing integrity of AI outputs', '94%', 'Partial'],
      ['PI1.2', 'SOC 2', 'Complete and accurate AI output recording', '99.5%', 'Ready'],
      ['CM-1', 'SOC 2', 'Change management for AI systems', '93%', 'Ready'],
      ['IR-1', 'SOC 2', 'Incident response for AI events', '100%', 'Ready'],
      ['ISO-6.1', 'ISO 42001', 'AI risk assessment documentation', '55%', 'Partial'],
      ['EU-AI-13.1', 'EU AI Act', 'Transparency documentation', '68%', 'Gap'],
    ],
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [99, 102, 241] },
  });
  apexFooter(doc);
  doc.save('apex-internal-audit-package.pdf');
  logDownload('apex-internal-audit-package.pdf', 'pdf', 'Auditor Workspace — Internal Audit');

  // Also export CSV population
  downloadCSV('apex-internal-audit-population.csv',
    ['Record ID', 'Timestamp', 'Model', 'Workflow', 'Control Mapping', 'Status'],
    populationRows,
    'Auditor Workspace — Internal Audit Population'
  );
}

export async function exportAuditPackageZIP(hasExtended) {
  const zip = new JSZip();

  // Population CSV
  const popHeaders = ['Record ID', 'Timestamp', 'Model', 'Workflow', 'Control Mapping', 'Status'];
  const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
  const popCSV = [popHeaders.map(escape).join(','), ...populationRows.map(r => r.map(escape).join(','))].join('\n');
  zip.file('population.csv', '\uFEFF' + popCSV);

  // Control matrix CSV
  const ctrlRows = [
    ['CC6.1', 'SOC 2', 'Logical access controls for AI systems', 'Agent Controls + Decision Ledger', '47832', '100%', 'Ready'],
    ['PI1.1', 'SOC 2', 'Processing integrity of AI outputs', 'Decision Ledger', '47832', '94%', 'Partial'],
    ['PI1.2', 'SOC 2', 'Complete and accurate AI output recording', 'Evidence Vault', '47614', '99.5%', 'Ready'],
    ['CM-1', 'SOC 2', 'Change management for AI systems', 'Change Control', '14', '93%', 'Ready'],
    ['IR-1', 'SOC 2', 'Incident response for AI events', 'Incident Review', '1', '100%', 'Ready'],
    ['ISO-6.1', 'ISO 42001', 'AI risk assessment documentation', 'Compliance Gap Register', '-', '55%', 'Partial'],
    ['EU-AI-13.1', 'EU AI Act', 'Transparency documentation', 'Decision Ledger + Roles', '8203', '68%', 'Gap'],
  ];
  const ctrlHeaders = ['Control ID', 'Framework', 'Description', 'Evidence Source', 'Records', 'Coverage', 'Status'];
  const ctrlCSV = [ctrlHeaders.map(escape).join(','), ...ctrlRows.map(r => r.map(escape).join(','))].join('\n');
  zip.file('control-matrix.csv', '\uFEFF' + ctrlCSV);

  // Exception report PDF
  const exDoc = new jsPDF();
  apexHeader(exDoc, 'Exception Report', 'Integrity warnings, control gaps, and open exceptions');
  addTable(exDoc, {
    startY: 60,
    head: [['ID', 'Type', 'Description', 'Severity', 'Status']],
    body: [
      ['EXC-001', 'Integrity Warning', 'Hash mismatch — DER-2026-031204', 'Medium', 'Open'],
      ['EXC-002', 'Control Gap', 'External Legal Corpus unverified', 'High', 'Open'],
      ['EXC-003', 'Control Gap', 'Fine-tune pipeline unverified', 'High', 'Open'],
      ['EXC-004', 'Policy Exception', 'GPT-4o Mini unaudited', 'Critical', 'Pending'],
    ],
    theme: 'grid',
    styles: { fontSize: 8 },
    headStyles: { fillColor: [245, 158, 11] },
  });
  apexFooter(exDoc);
  zip.file('exception-report.pdf', exDoc.output('blob'));

  // Governance narrative PDF
  const govDoc = new jsPDF();
  apexHeader(govDoc, 'AI Governance Narrative', 'Organizational structure and oversight processes');
  govDoc.setFontSize(10);
  govDoc.setTextColor(60, 60, 80);
  const narrative = [
    'Meridian Global maintains an AI governance framework under the oversight of the CISO and AI Ethics Board.',
    'All AI models are registered in the Apex AI Inventory before deployment. Each model undergoes risk classification',
    'and is assigned an owner responsible for ongoing compliance.',
    '',
    'Decision Evidence Records (DERs) are generated for every AI-assisted decision, cryptographically signed,',
    'and stored in the Evidence Vault with 7-year retention. Human review is required for high-risk classifications.',
    '',
    'Change control processes require approval before any model parameter, version, or configuration change.',
    'Incident response procedures activate automatically when unauthorized changes or output drift are detected.',
    '',
    'Framework coverage: EU AI Act (68%), ISO 42001 (55%), NIST AI RMF (71%), SOC 2 AI Controls (61%).',
  ];
  let ny = 60;
  narrative.forEach(line => { govDoc.text(line, 14, ny); ny += 6; });
  apexFooter(govDoc);
  zip.file('governance-narrative.pdf', govDoc.output('blob'));

  // Extended framework mapping if selected
  if (hasExtended) {
    const extRows = [
      ['ISO-A.12.4', 'ISO 27001', 'Logging and monitoring', 'Evidence Vault + Decision Ledger', '96%', 'Ready'],
      ['ISO-A.9.4', 'ISO 27001', 'Access control for AI systems', 'Agent Controls', '100%', 'Ready'],
      ['AU-2', 'NIST 800-53', 'Audit events for AI invocations', 'Evidence Vault', '99%', 'Ready'],
      ['AC-6', 'NIST 800-53', 'Least privilege for AI agents', 'Agent Controls', '92%', 'Ready'],
      ['CM-3', 'NIST 800-53', 'Change control for AI', 'Change Control', '93%', 'Ready'],
      ['NIS2-IR', 'NIS2', 'Incident reporting', 'Incident Review', '100%', 'Ready'],
      ['NIS2-LOG', 'NIS2', 'Logging obligations', 'Evidence Vault', '96%', 'Ready'],
    ];
    const extHeaders = ['Control ID', 'Framework', 'Description', 'Source', 'Coverage', 'Status'];
    const extCSV = [extHeaders.map(escape).join(','), ...extRows.map(r => r.map(escape).join(','))].join('\n');
    zip.file('extended-framework-mapping.csv', '\uFEFF' + extCSV);
  }

  const blob = await zip.generateAsync({ type: 'blob' });
  saveAs(blob, 'apex-audit-package.zip');
  logDownload('apex-audit-package.zip', 'zip', 'Auditor Workspace — Complete Audit Package');
}

/* ── Sector Profile Exports ───────────────────────────── */
export function exportSectorProfilePDF(sectorTitle) {
  const doc = new jsPDF();
  const y = apexHeader(doc, `Sector Profile: ${sectorTitle}`, 'Evidence mapping from existing Apex data');
  doc.setFontSize(9);
  doc.setTextColor(80);
  doc.text('This profile maps existing Apex evidence to sector-specific audit requirements.', 14, y + 4);
  doc.text('No additional data collection is required.', 14, y + 10);
  const mappings = {
    'Financial Services (PCI DSS / FFIEC)': [
      ['PCI DSS 6.4', 'Change control for AI in payment workflows', 'Change Control', '93%'],
      ['PCI DSS 10.1', 'Audit trails for AI decisions', 'Evidence Vault', '99.5%'],
      ['FFIEC BSA/AML', 'AI model risk management', 'AI Inventory + Risk Score', '78%'],
    ],
    'Healthcare (HIPAA / HITRUST)': [
      ['HIPAA 164.312(b)', 'Audit controls for AI processing PHI', 'Evidence Vault', '99.5%'],
      ['HITRUST 09.ab', 'Monitoring AI system access', 'Agent Controls', '100%'],
      ['HIPAA 164.308(a)(6)', 'AI incident response', 'Incident Review', '100%'],
    ],
    'US Federal / FedRAMP': [
      ['NIST 800-53 AU-2', 'AI audit events', 'Evidence Vault', '99%'],
      ['NIST 800-53 AC-6', 'AI agent access controls', 'Agent Controls', '92%'],
      ['FedRAMP Rev 5 CM-3', 'AI change management', 'Change Control', '93%'],
    ],
    'EU Network & Information Security (NIS2)': [
      ['NIS2 Art. 21', 'AI risk management measures', 'Risk Score + Compliance', '71%'],
      ['NIS2 Art. 23', 'AI incident reporting', 'Incident Review', '100%'],
      ['NIS2 Art. 21(2)(g)', 'AI system logging', 'Evidence Vault', '96%'],
    ],
  };
  addTable(doc, {
    startY: y + 16,
    head: [['Requirement', 'Description', 'Apex Source', 'Coverage']],
    body: mappings[sectorTitle] || [['—', 'No specific mappings available', '—', '—']],
    theme: 'grid',
    styles: { fontSize: 8, cellPadding: 3 },
    headStyles: { fillColor: [99, 102, 241] },
  });
  apexFooter(doc);
  const fn = `apex-sector-${sectorTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-')}.pdf`;
  doc.save(fn);
  logDownload(fn, 'pdf', `Assurance Center — ${sectorTitle}`);
}
